import React, { useState, useEffect } from 'react';
import { Icons } from '@ohif/ui-next';
import { Button } from '@ohif/ui-next';

export enum showDialogOption {
  NeverShowDialog = 'never',
  AlwaysShowDialog = 'always',
  ShowOnceAndConfigure = 'configure',
}

/**
 * develop:platform/ui/src/components/InvestigationalUseDialog/InvestigationalUseDialog.tsx,
 * replayed for copy, the guideline link and the gradient CTA -- SYCAI's own product/legal
 * text, not upstream OHIF's "OHIF Viewer is for investigational use only" banner. Ported onto
 * ui-next's Button/Icons (develop's file predates the ui-next migration and used the legacy
 * platform/ui Button); the shell keeps this fork's --popover/--muted tokens rather than
 * develop's literal bg-secondary-main/border-secondary-main, since M5.5 D9 rule 4 already
 * measured those tokens equal to develop's values. The gradient is inline, matching develop's
 * own inline from-/to- stops -- no other surface in the fork uses a gradient CTA, so there is
 * no shared token to route it through.
 */
const InvestigationalUseDialog = ({
  dialogConfiguration = {
    option: showDialogOption.AlwaysShowDialog,
  },
}) => {
  const { option, days } = dialogConfiguration;
  const [isHidden, setIsHidden] = useState(true);

  useEffect(() => {
    const dialogLocalState = localStorage.getItem('investigationalUseDialog');
    const dialogSessionState = sessionStorage.getItem('investigationalUseDialog');

    switch (option) {
      case showDialogOption.NeverShowDialog:
        setIsHidden(true);
        break;
      case showDialogOption.AlwaysShowDialog:
        setIsHidden(!!dialogSessionState);
        break;
      case showDialogOption.ShowOnceAndConfigure:
        if (dialogLocalState) {
          const { expiryDate } = JSON.parse(dialogLocalState);
          const isExpired = new Date() > new Date(expiryDate);
          setIsHidden(!isExpired);
        } else {
          setIsHidden(false);
        }
        break;
      default:
        setIsHidden(true);
    }
  }, [option, days]);

  const handleConfirmAndHide = () => {
    const expiryDate = new Date();

    switch (option) {
      case showDialogOption.ShowOnceAndConfigure:
        expiryDate.setDate(expiryDate.getDate() + days);
        localStorage.setItem('investigationalUseDialog', JSON.stringify({ expiryDate }));
        break;
      case showDialogOption.AlwaysShowDialog:
        sessionStorage.setItem('investigationalUseDialog', 'hidden');
        break;
    }
    setIsHidden(true);
  };

  if (isHidden) {
    return null;
  }

  return (
    <div className="fixed bottom-2 z-50 flex h-[86px] w-full justify-center">
      <div className="bg-popover border-muted flex w-[90%] items-center justify-between rounded-lg border-2 pl-[22px] pr-[22px] pt-[10px] pb-[10px] shadow-lg">
        <div className="flex items-center gap-4">
          <Icons.InvestigationalUse className="h-18 w-18 investigational-use-icon" />
          <div className="flex flex-col">
            <div className="text-highlight text-[19px]">
              This tool is provided for demonstration purposes only. It is not intended for
              clinical, or diagnostic use.
            </div>
            <div className="text-foreground text-[13px]">
              <span
                className="text-primary cursor-pointer"
                onClick={() => window.open('https://sycaimedical.com/', '_blank')}
              >
                Read the user guideline first. Sale, distribution, or usage beyond the agreed
                testing environment is strictly prohibited. Features of this tool may be subject
                to change without notice.
              </span>
            </div>
          </div>
        </div>
        <Button
          onClick={handleConfirmAndHide}
          dataCY="confirm-and-hide-button"
          className="rounded-lg bg-gradient-to-r from-[#CD4A29] to-[#21357E] text-white hover:!bg-primary hover:[background-image:none]"
        >
          Confirm and hide
        </Button>
      </div>
    </div>
  );
};

export default InvestigationalUseDialog;
